/**
 * The chart: column headings, the gridline/today overlay, and the stack of lanes.
 *
 * The overlay is a single element covering every lane rather than gridlines drawn
 * per row. Drawn per row they break wherever a row has a border or an editor is
 * open, and the today line - the one fixed reference the eye uses to judge whether a
 * bar is late - turns into a dashed column of unequal segments. One overlay also
 * guarantees the line and the bars share an origin: see the note in parts.ts.
 */

import styled from 'styled-components';

import { palette } from '../../styles/theme';
import { formatLong, placeDay, type Grid } from '../../utils/dates';
import type { Person, Phase, Project, ProjectPatch } from '../../types';
import Lane from './Lane';
import {
  ChartFrame,
  ChartScroll,
  HeaderLabelCell,
  HeaderOverlay,
  HeaderRow,
  HeaderTrack,
  MonthCell,
  Overlay,
  TickRow,
  TodayFlag,
  TodayLine,
  WeekCell,
} from './parts';

/** DOM id of a lane, so other panels can scroll to it. */
export function laneAnchorId(projectId: string): string {
  return `lane-${projectId}`;
}

const Empty = styled.p`
  margin: 0;
  padding: 28px 8px;
  text-align: center;
  color: ${palette.inkSoft};
`;

export interface TimelineProps {
  projects: Project[];
  people: Person[];
  grid: Grid;
  today: string;
  expanded: ReadonlySet<string>;
  onToggle: (projectId: string) => void;
  onPhaseSaved: (phase: Phase) => void;
  onProjectSaved: (projectId: string, patch: ProjectPatch) => void;
}

export default function Timeline({
  projects,
  people,
  grid,
  today,
  expanded,
  onToggle,
  onPhaseSaved,
  onProjectSaved,
}: TimelineProps) {
  const todayPct = placeDay(grid, today);
  // buildGrid always widens the span to contain today, so this is belt and braces -
  // but a marker drawn at -14% is invisible and silently wrong rather than absent,
  // and that is the sort of thing nobody notices for a month.
  const todayVisible = todayPct >= 0 && todayPct <= 100;

  return (
    <ChartScroll>
      <ChartFrame>
        <HeaderRow>
          <HeaderLabelCell>Project</HeaderLabelCell>
          <HeaderTrack>
            <TickRow>
              {grid.columns.map((column) => (
                <MonthCell key={`m-${column.key}`}>{column.monthLabel ?? ''}</MonthCell>
              ))}
            </TickRow>
            <TickRow>
              {grid.columns.map((column) => (
                // The day-of-month of each Monday. Weeks rather than days because a
                // roadmap that runs five months has no use for 147 columns, and
                // rather than week numbers because nobody in the firm thinks in
                // ISO weeks - "the week of the 14th" is how these are discussed.
                <WeekCell key={`w-${column.key}`}>{column.day}</WeekCell>
              ))}
            </TickRow>
          </HeaderTrack>
        </HeaderRow>

        <Overlay $columns={grid.columns.length} aria-hidden>
          {todayVisible ? <TodayLine $leftPct={todayPct} /> : null}
        </Overlay>

        {todayVisible ? (
          <HeaderOverlay aria-hidden>
            <TodayFlag $leftPct={todayPct}>
              <span title={formatLong(today)}>Today</span>
            </TodayFlag>
          </HeaderOverlay>
        ) : null}

        {projects.length === 0 ? (
          <Empty>No projects match the current filters.</Empty>
        ) : (
          projects.map((project) => (
            // The wrapper exists so a gap in the side panel can scroll its lane into
            // view. Lane itself renders several sibling rows, so there is no single
            // element to hang the anchor on from the inside.
            <div key={project.project_id} id={laneAnchorId(project.project_id)}>
              <Lane
                project={project}
                grid={grid}
                people={people}
                today={today}
                expanded={expanded.has(project.project_id)}
                onToggle={() => onToggle(project.project_id)}
                onPhaseSaved={onPhaseSaved}
                onProjectSaved={onProjectSaved}
              />
            </div>
          ))
        )}
      </ChartFrame>
    </ChartScroll>
  );
}
